
import { Component, Prop, Vue } from "vue-property-decorator";

import subscriptions_routes from "@/api/routes/subscriptions";

import Preloader from "@/components/Preloader.vue";
import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import {
  IDataPrograms,
  IStudentProgramsData
} from "@/models/subscriptions/program";

import { ESubscriptionProgressStatus } from "@/models/subscriptions/status";

import CUserCatalogSubscription from "./components/CatalogSubscription.vue";

export interface IDataProgress {
  id: ESubscriptionProgressStatus;
  name: string;
  clazz: string;
}

@Component({
  name: "VUserCatalog",
  components: {
    Preloader,
    CInfiniteScroll,
    CUserCatalogSubscription
  }
})
export default class VUserCatalog extends Vue {
  @Prop({ required: false }) public tab_id?: number;

  public preload: boolean = false;

  private preload_infinite_horizon: boolean = false;
  private preload_infinite_unscheduled: boolean = false;
  private preload_infinite_completed: boolean = false;

  private per_page: number = 100;

  private current_page_horizon: number = 0;
  private next_page_horizon: boolean = false;

  private current_page_unscheduled: number = 0;
  private next_page_unscheduled: boolean = false;

  private current_page_completed: number = 0;
  private next_page_completed: boolean = false;

  public horizon_subscriptions: IStudentProgramsData[] = [];
  public unscheduled_subscriptions: IStudentProgramsData[] = [];
  public completed_subscriptions: IStudentProgramsData[] = [];

  private current_user: IJWTUser | null = this.$store.state.currentUser;

  public tabs: string[] = [
    "Индивидуальный план развития",
    "Внеплановое обучение",
    "ИПР. Завершенные"
  ];
  public current_tab_idx: number = 0;

  private progress: IDataProgress[] = [
    {
      id: ESubscriptionProgressStatus.SLIGHTLY_BEHIND,
      name: "Немного отстает",
      clazz: "font-orange"
    },
    {
      id: ESubscriptionProgressStatus.FAR_BEHIND,
      name: "Сильно отстает",
      clazz: "font-red"
    },
    {
      id: ESubscriptionProgressStatus.MAKE_PROGRESS,
      name: "Успевает",
      clazz: "font-blue"
    }
  ];
  public current_progress: IDataProgress | null = null;

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.preload = true;

    if (this.tab_id != null) {
      this.current_tab_idx = this.tab_id;
    }

    await Promise.all([
      this.getProgress(),
      this.loadHorizonSubscriptions(),
      this.loadUnscheduledSubscriptions(),
      this.loadCompletedSubscriptions()
    ]).finally(() => (this.preload = false));
  }

  protected async getProgress() {
    return this.$api
      .get(subscriptions_routes.progress, {
        params: {
          id: this.current_user!.id
        }
      })
      .then(
        ({
          data: res
        }: {
          data: { progress_status: ESubscriptionProgressStatus };
        }) => {
          this.current_progress =
            this.progress.find(i => i.id === res.progress_status) ?? null;
        }
      );
  }

  public async infiniteHandlerHorizon() {
    if (this.next_page_horizon && !this.preload_infinite_horizon) {
      await this.loadHorizonSubscriptions(this.current_page_horizon + 1);
    }
  }

  public async infiniteHandlerUnscheduled() {
    if (this.next_page_unscheduled && !this.preload_infinite_unscheduled) {
      await this.loadUnscheduledSubscriptions(
        this.current_page_unscheduled + 1
      );
    }
  }

  public async infiniteHandlerCompleted() {
    if (this.next_page_completed && !this.preload_infinite_completed) {
      await this.loadCompletedSubscriptions(this.current_page_completed + 1);
    }
  }

  private async loadHorizonSubscriptions(page: number = 0) {
    this.preload_infinite_horizon = true;

    return this.$api
      .get(subscriptions_routes.assignment_programs(this.current_user!.id), {
        params: {
          page,
          per_page: this.per_page,
          completion_status: "not_completed"
        }
      })
      .then(({ data: res }: { data: IDataPrograms }) => {
        this.current_page_horizon = res.current_page;
        this.next_page_horizon = res.next_page;
        this.horizon_subscriptions.push(...res.programs);
      })
      .finally(() => {
        this.preload_infinite_horizon = false;
      });
  }

  private async loadCompletedSubscriptions(page: number = 0) {
    this.preload_infinite_completed = true;

    return this.$api
      .get(subscriptions_routes.assignment_programs(this.current_user!.id), {
        params: {
          page,
          per_page: this.per_page,
          completion_status: "completed"
        }
      })
      .then(({ data: res }: { data: IDataPrograms }) => {
        this.current_page_completed = res.current_page;
        this.next_page_completed = res.next_page;
        this.completed_subscriptions.push(...res.programs);
      })
      .finally(() => {
        this.preload_infinite_completed = false;
      });
  }

  private async loadUnscheduledSubscriptions(page: number = 0) {
    this.preload_infinite_unscheduled = true;

    return this.$api
      .get(
        subscriptions_routes.assignment_programs_unscheduled(
          this.current_user!.id
        ),
        {
          params: {
            page,
            per_page: this.per_page,
            completion_status: "not_completed"
          }
        }
      )
      .then(({ data: res }: { data: IDataPrograms }) => {
        this.current_page_unscheduled = res.current_page;
        this.next_page_unscheduled = res.next_page;
        this.unscheduled_subscriptions.push(...res.programs);
      })
      .finally(() => {
        this.preload_infinite_unscheduled = false;
      });
  }
}
